/* __next_internal_client_entry_do_not_use__ Default,accordionListTailwindVariants auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Global
import { useMemo } from 'react';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { AccordionContextProvider, useAccordionContext } from 'helpers/Context/AccordionContext';
import { findComponent } from 'lib/utils/object-utils';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
// Expand/Collapse All Buttons Component
const AccordionControls = ()=>{
    const { openPanels, panels, expandAll, collapseAll } = useAccordionContext();
    const { getDictionaryValue } = useDictionary();
    const allPanelIds = panels.map((panel)=>panel.uid ?? '').filter(Boolean);
    const allOpen = allPanelIds.length > 0 && allPanelIds.every((id)=>openPanels.some((panel)=>panel.uid === id));
    const someOpen = openPanels.length > 0;
    const { expandCollapseBtn, expandCollapseBtnActive, expandCollapseBtnInactive, divider, controlsContainer } = accordionListTailwindVariants();
    return /*#__PURE__*/ _jsxs("div", {
        className: controlsContainer(),
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: expandAll,
                disabled: allOpen,
                className: expandCollapseBtn() + ' ' + (allOpen ? expandCollapseBtnInactive() : expandCollapseBtnActive()),
                "aria-label": "Expand all accordion items",
                children: getDictionaryValue('AccordionExpandAll') || 'Expand All'
            }),
            /*#__PURE__*/ _jsx("span", {
                className: divider()
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: collapseAll,
                disabled: !someOpen,
                className: expandCollapseBtn() + ' ' + (!someOpen ? expandCollapseBtnInactive() : expandCollapseBtnActive()),
                "aria-label": "Collapse all accordion items",
                children: getDictionaryValue('AccordionCollapseAll') || 'Collapse All'
            })
        ]
    });
};
const Accordion = (props)=>{
    const { page } = useSitecore();
    const isEditing = page?.mode?.isEditing;
    const { DynamicPlaceholderId, RenderingIdentifier } = props?.params || {};
    const phKey = `accordion-${DynamicPlaceholderId}`;
    const accordionItems = findComponent(props.rendering, 'AccordionItem');
    const singlePanelOpen = props.params.singleOpenPanel === '1';
    const scrollToOpenPanel = props.params.scrollToOpenPanel === '1';
    const twoColumn = props?.params?.twoColumn === '1';
    const defaultOpenPanels = [];
    if (props.params.openByDefault === '1') {
        // Open By Default should only open the first panel
        defaultOpenPanels.push(accordionItems[0]);
    }
    // Convert HTML to plain text for JSON-LD answers.
    // Must be isomorphic (no window/DOM) so SSR and client produce identical markup.
    const convertHtmlToPlainText = (html)=>{
        if (!html) return '';
        return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    };
    const faqSchema = useMemo(()=>{
        const entities = accordionItems.reduce((acc, item)=>{
            const heading = item?.fields?.heading;
            const content = item?.fields?.content;
            const name = heading?.value || '';
            const answerHtml = content?.value || '';
            const text = convertHtmlToPlainText(answerHtml);
            if (name && text) {
                acc.push({
                    '@type': 'Question',
                    name,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text
                    }
                });
            }
            return acc;
        }, []);
        if (entities.length === 0) return null;
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: entities
        };
    }, [
        accordionItems
    ]);
    const { base, container, controls, twoColumnContainer, singleColumnContainer, accordionGrid, accordionGridSingle, accordionColumn } = accordionListTailwindVariants();
    return /*#__PURE__*/ _jsxs("section", {
        className: base(),
        "data-component": "authorable/shared/lists/accordion",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-accordion-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsx(AccordionContextProvider, {
                panels: accordionItems,
                singleOpenPanel: singlePanelOpen,
                scrollToOpenPanel: scrollToOpenPanel,
                defaultOpenPanels: defaultOpenPanels,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: container(),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: controls(),
                            children: !singlePanelOpen && /*#__PURE__*/ _jsx(AccordionControls, {})
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: twoColumn ? twoColumnContainer() : singleColumnContainer(),
                            children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                name: phKey,
                                rendering: props.rendering,
                                render: (components)=>{
                                    if (twoColumn) {
                                        // Two column layout - split into left and right columns
                                        const leftColumnItems = [];
                                        const rightColumnItems = [];
                                        // Single pass through components to split into columns
                                        components.forEach((comp, index)=>{
                                            if (comp) {
                                                const accordionItem = /*#__PURE__*/ _jsx("div", {
                                                    children: comp
                                                }, index);
                                                if (index % 2 === 0) {
                                                    leftColumnItems.push(accordionItem);
                                                } else {
                                                    rightColumnItems.push(accordionItem);
                                                }
                                            }
                                        });
                                        return /*#__PURE__*/ _jsxs("div", {
                                            className: accordionGrid(),
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: accordionColumn(),
                                                    children: leftColumnItems
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: accordionColumn(),
                                                    children: rightColumnItems
                                                })
                                            ]
                                        });
                                    }
                                    // Single column - render normally
                                    return /*#__PURE__*/ _jsx("div", {
                                        className: accordionGridSingle(),
                                        children: components.map((comp, index)=>comp ? /*#__PURE__*/ _jsx("div", {
                                                children: comp
                                            }, index) : null)
                                    });
                                },
                                ...getTestProps(`accordion-ph`)
                            })
                        })
                    ]
                })
            }),
            !isEditing && faqSchema && /*#__PURE__*/ _jsx("script", {
                type: "application/ld+json",
                dangerouslySetInnerHTML: {
                    __html: JSON.stringify(faqSchema)
                }
            })
        ]
    });
};
export const Default = withStandardComponentWrapper(Accordion, false);
export const accordionListTailwindVariants = tv({
    defaultVariants: {
        twoColumn: false
    },
    slots: {
        base: [
            ''
        ],
        container: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ],
        controls: [
            'flex',
            'justify-between',
            'mb-spacing-spacing-4',
            'w-full'
        ],
        expandCollapseBtn: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'py-0',
            'border-none',
            'outline-none',
            'focus:underline',
            'transition-colors',
            'cursor-pointer',
            'hover:text-component-accordion-toggle-hover',
            'hover:underline',
            'focus:text-component-accordion-toggle-hover',
            'focus:ring',
            'focus:ring-offset-1',
            'focus:ring-color-border-border-focus',
            'rounded-component-accordion-item-border-radius'
        ],
        expandCollapseBtnActive: [
            'text-component-accordion-toggle-active',
            'font-semibold'
        ],
        expandCollapseBtnInactive: [
            'text-component-accordion-toggle',
            'font-normal',
            'disabled:cursor-not-allowed'
        ],
        divider: [
            'border-l',
            'border-component-accordion-divider',
            'mx-2',
            'h-5',
            'inline-block',
            'align-middle'
        ],
        controlsContainer: [
            'flex',
            'items-center',
            'gap-0',
            'w-full',
            'justify-end'
        ],
        twoColumnContainer: [
            'w-full'
        ],
        singleColumnContainer: [
            'w-full'
        ],
        accordionGrid: [
            'grid',
            'gap-spacing-spacing-8',
            'md:grid-cols-2',
            'md:gap-spacing-spacing-24'
        ],
        accordionGridSingle: [
            'grid',
            'gap-spacing-spacing-8'
        ],
        accordionColumn: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ]
    }
});
